import { PALLET_TYPES } from '../../constants/pallets';

// Step 1: Products Load types
export interface WarehouseStock {
  warehouseId: string;
  warehouseName: string;
  storedQty: number;
  address: string;
  availability: 'Available' | 'N/A';
  status: 'Active' | 'Non-engaged' | 'Inactive' | 'Quarantined';
}

export interface ProductItem {
  id: string;
  skuId: string;
  name: string;
  image?: string;
  inStock: number;
  totalQty: number;
  weight: number;
  selectedQty: number;
  palletType: string;
  warehouseStock: WarehouseStock[];
  isSelected: boolean;
  isExpanded: boolean;
  isUnavailable?: boolean;
}

export interface PalletSummary {
  type: string;
  dimensions: string;
  quantity: number;
  weight: number;
  volume: number;
}

export interface LoadSummary {
  totalSKUs: number;
  totalUnits: number;
  totalPallets: number;
  palletBreakdown: PalletSummary[];
  totalWeight: number;
  unavailableProducts: ProductItem[];
}

// Step 2: Shipment details types
export interface ShipmentMethodOption {
  id: string;
  title: string;
  description: string;
  price: string;
  recommended?: boolean;
}

export interface WarehouseOption {
  id: string;
  name: string;
  address: string;
}

// Form values
export interface NewOutboundShipmentFormValues {
  // Step 1: Products Load
  selectedProducts: ProductItem[];

  // Step 2: Enter shipment's details
  purchaseOrderNumber: string;
  shipmentId: string;

  // Origin
  originWarehouse: string;
  originWarehouseId: string;

  // Destination
  client: string;
  clientId: string;

  // Timing
  desiredPickupDate: string;
  desiredPickupTimeFrom: string;
  desiredPickupTimeTo: string;
  desiredDeliveryDate: string;
  desiredDeliveryTimeFrom: string;
  desiredDeliveryTimeTo: string;

  // Special Instructions
  specialInstructions: string;

  // Shipment Method
  shipmentMethod: string;

  // Drop-Off Site / Access Restrictions
  dropoffAccessRestrictions: string;
  dropoffSecurityInduction: string;
  dropoffPreBooking: string;
  dropoffWaitingTime: string;
  dropoffWaitingTimeUnit: string;

  // Vehicle and equipment requirements
  preferredVehicle: string;
  curtainSideOrBoxBody: string;
  tailLiftRequired: string;
  moffettRequired: string;
  minimumInternalHeight: string;
  minimumInternalHeightUnit: string;

  // Loading / Unloading on Site
  forkliftAtCollection: boolean;
  forkliftAtDelivery: boolean;
  dockLoading: boolean;
  handballRequired: boolean;
  palletTruckNeeded: boolean;
}

export const defaultFormValues: NewOutboundShipmentFormValues = {
  // Step 1: Products Load
  selectedProducts: [],

  // Step 2: Enter shipment's details
  purchaseOrderNumber: '',
  shipmentId: '',

  // Origin
  originWarehouse: '',
  originWarehouseId: '',

  // Destination
  client: '',
  clientId: '',

  // Timing
  desiredPickupDate: '',
  desiredPickupTimeFrom: '',
  desiredPickupTimeTo: '',
  desiredDeliveryDate: '',
  desiredDeliveryTimeFrom: '',
  desiredDeliveryTimeTo: '',

  // Special Instructions
  specialInstructions: '',

  // Shipment Method
  shipmentMethod: '',

  // Drop-Off Site / Access Restrictions
  dropoffAccessRestrictions: '',
  dropoffSecurityInduction: '',
  dropoffPreBooking: '',
  dropoffWaitingTime: '',
  dropoffWaitingTimeUnit: 'minutes',

  // Vehicle and equipment requirements
  preferredVehicle: '',
  curtainSideOrBoxBody: '',
  tailLiftRequired: 'no',
  moffettRequired: 'no',
  minimumInternalHeight: '',
  minimumInternalHeightUnit: 'm',

  // Loading / Unloading on Site
  forkliftAtCollection: false,
  forkliftAtDelivery: false,
  dockLoading: false,
  handballRequired: false,
  palletTruckNeeded: false,
};

// Pallet type options
export const palletTypeOptions = PALLET_TYPES.flatMap((type) =>
  type.footprint
    ? [
        {
          label: `${type.shortLabel} pallet`,
          value: type.value,
          dimensions: `${type.footprint.lengthCm * 10}×${
            type.footprint.widthCm * 10
          } mm`,
        },
      ]
    : []
);
